import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ChevronLeft, ChevronRight, Images, Inbox, LifeBuoy,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import {
  SUPPORT_ERRORS,
  TICKET_ORDER_CHANNEL,
  TICKET_ORDER_CHANNEL_LABELS,
  TICKET_RESOLUTION,
  formatTicketListSummary,
  formatTicketListTitle,
  listSupportTickets,
  updateSupportTicket,
} from '../../api/support';
import TicketDrawer from './TicketDrawer';
import { timeAgo } from '../../data/mockData';
import styles from './SupportRequests.module.css';

const PAGE_SIZE = 10;

const TABS = [
  { key: 'OPEN', label: 'Open' },
  { key: 'IN_PROGRESS', label: 'In Progress' },
  { key: 'CLOSED', label: 'Closed' },
];

const CHANNEL_TABS = [
  { key: 'ALL', label: 'All channels' },
  { key: TICKET_ORDER_CHANNEL.DELIVERY, label: TICKET_ORDER_CHANNEL_LABELS[TICKET_ORDER_CHANNEL.DELIVERY] },
  { key: TICKET_ORDER_CHANNEL.SUBS, label: TICKET_ORDER_CHANNEL_LABELS[TICKET_ORDER_CHANNEL.SUBS] },
];

const EMPTY_COPY = {
  OPEN: 'New support tickets from customers and drivers will show up here.',
  IN_PROGRESS: 'Tickets you are actively working on will appear here.',
  CLOSED: 'Tickets you have resolved will appear here for reference.',
};

function friendlyError(err) {
  const code = err?.code || err?.message;
  return SUPPORT_ERRORS[code] || err?.detail || err?.message || 'Something went wrong.';
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function SupportRequests() {
  const { user } = useAuth();
  const { notify, refreshTicketBadges } = useOrders();

  const [tab, setTab] = useState('OPEN');
  const [channelTab, setChannelTab] = useState('ALL');
  const [page, setPage] = useState(1);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const pageTopRef = useRef(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listSupportTickets(tab, user.idToken, { channel: channelTab });
      setTickets(list);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [tab, channelTab, user.idToken]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    setPage(1);
  }, [tab, channelTab]);

  const totalPages = Math.max(1, Math.ceil(tickets.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedTickets = useMemo(
    () => tickets.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [tickets, currentPage],
  );
  const rangeStart = tickets.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, tickets.length);

  const goToPage = (nextPage) => {
    setPage(nextPage);
    requestAnimationFrame(() => {
      pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const switchTab = (key) => {
    setTab(key);
    setSelectedTicket(null);
  };

  const handleRespond = async (ticket, { status, opsNote, resolution, creditAmount }) => {
    const updated = await updateSupportTicket(
      ticket.ticketId,
      {
        customerSub: ticket.customerSub,
        status,
        opsNote,
        resolution,
        creditAmount,
      },
      user.idToken,
    );
    setSelectedTicket(null);
    if (resolution === TICKET_RESOLUTION.DISPOSE) {
      const amount = updated.creditAmount ?? creditAmount;
      notify(
        `Ticket #${ticket.ticketId} closed · ₹${amount} credited to wallet`,
      );
    } else if (resolution === TICKET_RESOLUTION.REQUIRE_RETURN) {
      const rmas = updated?.returnIds?.length
        ? ` · RMA ${updated.returnIds.join(', ')}`
        : '';
      notify(`Ticket #${ticket.ticketId}: return required${rmas}`);
    } else {
      notify(`Ticket #${ticket.ticketId} updated`);
    }
    await loadTickets();
    refreshTicketBadges();
  };

  const renderTicketMeta = (t) => (
    <>
      <p className={styles.ticketSubject}>
        {formatTicketListTitle(t)}
        {t.orderChannel === TICKET_ORDER_CHANNEL.SUBS && (
          <span className={styles.channelBadgeSubs}>
            {TICKET_ORDER_CHANNEL_LABELS[TICKET_ORDER_CHANNEL.SUBS]}
          </span>
        )}
      </p>
      <p className={styles.ticketMessage} title={t.message}>
        {formatTicketListSummary(t)}
      </p>
      {t.imageUrls?.length > 0 && (
        <p className={styles.photoCount}>
          <Images size={11} />
          {t.imageUrls.length} photo{t.imageUrls.length === 1 ? '' : 's'}
        </p>
      )}
    </>
  );

  return (
    <div ref={pageTopRef}>
      <PageHeader
        eyebrow="Admin · Ops"
        title="Support Requests"
        subtitle="Review and respond to support tickets raised by customers and drivers."
      />

      <div className={styles.filterRow}>
        <div className={styles.filterTabs}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${styles.filterTab} ${tab === key ? styles.filterTabActive : ''}`}
              onClick={() => switchTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.channelFilterRow}>
        <span className={styles.channelFilterLabel}>Channel</span>
        <div className={styles.channelTabs}>
          {CHANNEL_TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${styles.channelTab} ${channelTab === key ? styles.channelTabActive : ''}`}
              onClick={() => {
                setChannelTab(key);
                setSelectedTicket(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className={styles.loadingText}>Loading tickets…</p>}

      {!loading && error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load support tickets"
          description={error}
          action={<Button variant="outline" onClick={loadTickets}>Retry</Button>}
        />
      )}

      {!loading && !error && tickets.length === 0 && (
        <EmptyState
          icon={Inbox}
          title={`No ${TABS.find((t) => t.key === tab).label.toLowerCase()} tickets`}
          description={
            channelTab === TICKET_ORDER_CHANNEL.SUBS
              ? 'No subscription-linked support tickets match this status. Try All channels, or check Return & Exchange for RMAs.'
              : EMPTY_COPY[tab]
          }
        />
      )}

      {!loading && !error && tickets.length > 0 && (
        <div className={styles.listArea}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Customer</th>
                  <th>Order</th>
                  <th>Raised</th>
                  <th>Status</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {pagedTickets.map((t) => (
                  <tr key={t.ticketId}>
                    <td>
                      <div className={styles.ticketCell}>
                        <div className={styles.ticketIcon}><LifeBuoy size={16} /></div>
                        <div className={styles.ticketMeta}>{renderTicketMeta(t)}</div>
                      </div>
                    </td>
                    <td title={t.customerSub}>
                      <span className={styles.subCell}>{t.customerSub}</span>
                    </td>
                    <td className={styles.orderCell} title={t.orderId || undefined}>
                      {t.orderId ? (
                        <span className={styles.orderTag}>#{t.orderId}</span>
                      ) : <span className={styles.muted}>—</span>}
                    </td>
                    <td className={styles.timeCell} title={formatDate(t.createdAt)}>
                      {timeAgo(t.createdAt)}
                    </td>
                    <td><StatusBadge status={t.status} size="sm" /></td>
                    <td className={styles.actionsCell}>
                      <Button variant="outline" size="sm" onClick={() => setSelectedTicket(t)}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.mobileList} aria-label="Support tickets">
            {pagedTickets.map((t) => (
              <article key={t.ticketId} className={styles.mobileCard}>
                <div className={styles.mobileCardTop}>
                  <div className={styles.ticketCell}>
                    <div className={styles.ticketIcon}><LifeBuoy size={16} /></div>
                    <div className={styles.ticketMeta}>{renderTicketMeta(t)}</div>
                  </div>
                  <StatusBadge status={t.status} size="sm" />
                </div>

                <div className={styles.mobileMetaRow}>
                  <span className={styles.subCell} title={t.customerSub}>{t.customerSub}</span>
                  {t.orderId ? (
                    <span className={styles.orderTag} title={t.orderId}>#{t.orderId}</span>
                  ) : (
                    <span className={styles.muted}>No order</span>
                  )}
                </div>

                <div className={styles.mobileCardFooter}>
                  <span className={styles.timeCell} title={formatDate(t.createdAt)}>
                    {timeAgo(t.createdAt)}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setSelectedTicket(t)}>
                    View
                  </Button>
                </div>
              </article>
            ))}
          </div>

          <div className={styles.paginationBar}>
            <span className={styles.paginationSummary}>
              Showing {rangeStart}–{rangeEnd} of {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
            </span>
            <div className={styles.paginationControls}>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => goToPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </button>
              <span className={styles.paginationPage}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      <TicketDrawer
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
        onRespond={handleRespond}
      />
    </div>
  );
}
